import { useMemo } from 'react';

import sanitiseProps from '/shared/util/sanitiseProps';

import DropdownOption from '/shared/components/widgets/form/DropdownOption';

import StyledInputWrapper from './__styled__/StyledInputWrapper';
import StyledInput from '/shared/styled/components/StyledInput';
import StyledInputDropdown from '/shared/styled/components/StyledInputDropdown';

interface Props {
  dropdownItems?: { name: string; displayName: string; isPlaceholder?: boolean }[];
  dropdownValue?: string;
  dropdownFirst?: boolean;
  testId?: string | null;
  onDropdownChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  $ref?: React.Ref<HTMLInputElement>;
  $inline?: boolean;
  $isErrored?: boolean;
  $highlight?: boolean;
  $dropdownSingleNoBorder?: boolean;
  $dropdownSizeMultiplier?: number;
}

function Input(props: Props & React.InputHTMLAttributes<HTMLInputElement>) {
  // I'm aware that prop spreading is bad but in this situation
  // it seems to just make sense tbh.

  const {
    dropdownItems = null,
    dropdownValue,
    dropdownFirst = false,
    testId = null,
    disabled = false,
    onDropdownChange,
    $ref = null,
    $isErrored,
    $dropdownSingleNoBorder = false,
    $dropdownSizeMultiplier = 4,
    type,
  } = props;

  const dropdownDisplayValue = useMemo(() => {
    const selected = (dropdownItems || []).filter((item) => item.name === dropdownValue);
    return selected.length ? selected[0].displayName : null;
  }, [dropdownValue, dropdownItems]);

  let hasDropdownItemsElement = false;
  let hasDropdownSingular = false;
  if (dropdownItems && Array.isArray(dropdownItems)) {
    hasDropdownItemsElement = true;
    if (dropdownItems.length === 1) {
      hasDropdownSingular = true;
    }
  }

  // determine if the selected dropdown value is a placeholder
  const isDropDownPlaceholder = dropdownItems?.some((item) => item.isPlaceholder && item?.name === dropdownValue);

  const blurOnWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number') {
      // if input type === "number" blur on mouse wheel scroll. Otherwise value changes as user scrolls to save changes in the form.
      return event.currentTarget.blur();
    }
    return null;
  };
  return (
    <StyledInputWrapper>
      {dropdownFirst && hasDropdownSingular && hasDropdownItemsElement && (
        <StyledInputDropdown
          $hasGrayBorder
          $hasArrow={false}
          value={dropdownValue}
          $displayValue={dropdownDisplayValue}
          as="div"
          disabled={disabled}
          $isDropDownPlaceholder={isDropDownPlaceholder}
          $isErrored={$isErrored}
          $dropdownSingleNoBorder={$dropdownSingleNoBorder}
          $hasDropdownFirst={dropdownFirst}
        >
          {dropdownItems?.map((dropdownItem) => (
            <DropdownOption
              key={dropdownItem.name || JSON.stringify(dropdownItem)}
              isVisualOnly
              dropdownItem={dropdownItem}
            />
          ))}
        </StyledInputDropdown>
      )}
      <StyledInput
        {...sanitiseProps(props)}
        ref={$ref}
        data-test-id={testId}
        $hasDropdown={hasDropdownItemsElement}
        $dropdownSingleNoBorder={$dropdownSingleNoBorder}
        $hasDropdownFirst={dropdownFirst}
        onWheel={(e) => blurOnWheel(e)}
      />
      {!hasDropdownSingular && hasDropdownItemsElement && (
        <StyledInputDropdown
          data-test-id={`${testId}-dropdown`}
          value={dropdownValue}
          $displayValue={dropdownDisplayValue}
          disabled={disabled}
          $isDropDownPlaceholder={isDropDownPlaceholder}
          $isErrored={$isErrored}
          $baseMultiplier={$dropdownSizeMultiplier}
          onChange={onDropdownChange}
        >
          {dropdownItems?.map((dropdownItem) => (
            <DropdownOption key={dropdownItem.name || JSON.stringify(dropdownItem)} dropdownItem={dropdownItem} />
          ))}
        </StyledInputDropdown>
      )}
      {!dropdownFirst && hasDropdownSingular && hasDropdownItemsElement && (
        <StyledInputDropdown
          $hasGrayBorder
          $hasArrow={false}
          value={dropdownValue}
          $displayValue={dropdownDisplayValue}
          as="div"
          disabled={disabled}
          $isDropDownPlaceholder={isDropDownPlaceholder}
          $isErrored={$isErrored}
          $dropdownSingleNoBorder={$dropdownSingleNoBorder}
        >
          {dropdownItems?.map((dropdownItem) => (
            <DropdownOption
              key={dropdownItem.name || JSON.stringify(dropdownItem)}
              isVisualOnly
              dropdownItem={dropdownItem}
            />
          ))}
        </StyledInputDropdown>
      )}
    </StyledInputWrapper>
  );
}

export default Input;
